/**
 * src/shell/portal/components/HumanPulseCanvas.tsx
 *
 * Interactive Human Pulse Kinetic Verification Canvas.
 * Non-camera, zero-surveillance proof-of-human-attention challenge.
 *
 * Users trace an ethereal path from Start to Goal.
 * High-frequency pointer coordinates are evaluated by the 5-signal classifier.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  HumanPulseClassifier,
  TrajectoryPoint,
  ClassificationReport,
} from '../../../domain/attention/human-pulse-classifier';
import { Sparkles, CheckCircle2, RotateCcw, ShieldCheck, AlertCircle } from 'lucide-react';

export interface HumanPulseCanvasProps {
  onVerified: (report: ClassificationReport) => void;
  onCancel?: () => void;
  rewardAmountTokens?: number;
}

export const HumanPulseCanvas: React.FC<HumanPulseCanvasProps> = ({
  onVerified,
  onCancel,
  rewardAmountTokens = 3,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isTracing, setIsTracing] = useState<boolean>(false);
  const [points, setPoints] = useState<TrajectoryPoint[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [status, setStatus] = useState<'idle' | 'tracing' | 'verified' | 'failed'>('idle');
  const [report, setReport] = useState<ClassificationReport | null>(null);

  // Path anchors (start -> control curve -> end)
  const startPos = { x: 40, y: 110 };
  const goalPos = { x: 340, y: 110 };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 1. Draw subtle background guide curve
    ctx.beginPath();
    ctx.moveTo(startPos.x, startPos.y);
    ctx.quadraticCurveTo(190, 30, goalPos.x, goalPos.y);
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.2)';
    ctx.lineWidth = 14;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Inner guide dash
    ctx.beginPath();
    ctx.moveTo(startPos.x, startPos.y);
    ctx.quadraticCurveTo(190, 30, goalPos.x, goalPos.y);
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.4)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 6]);
    ctx.stroke();
    ctx.setLineDash([]);

    // 2. Draw user's live kinetic trace
    if (points.length > 1) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = '#0284c7';
      ctx.shadowBlur = 8;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // 3. Draw Start Node
    ctx.beginPath();
    ctx.arc(startPos.x, startPos.y, 14, 0, Math.PI * 2);
    ctx.fillStyle = '#0284c7';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    // 4. Draw Goal Node
    ctx.beginPath();
    ctx.arc(goalPos.x, goalPos.y, 16, 0, Math.PI * 2);
    ctx.fillStyle = status === 'verified' ? '#10b981' : '#f59e0b';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
  }, [points, status, startPos.x, startPos.y, goalPos.x, goalPos.y]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (status === 'verified') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check proximity to start anchor
    const distToStart = Math.hypot(x - startPos.x, y - startPos.y);
    if (distToStart > 30) {
      return; // Must initiate from start circle
    }

    const now = performance.now();
    setIsTracing(true);
    setStatus('tracing');
    setStartTime(now);
    setPoints([{ x, y, t: 0 }]);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isTracing || !startTime) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const elapsed = Math.round(performance.now() - startTime);

    setPoints((prev) => [...prev, { x, y, t: elapsed }]);
  };

  const handlePointerUp = () => {
    if (!isTracing || !startTime) return;
    setIsTracing(false);

    const duration = Math.round(performance.now() - startTime);
    const lastPoint = points[points.length - 1];

    // Check if user reached proximity of goal anchor
    const distToGoal = lastPoint ? Math.hypot(lastPoint.x - goalPos.x, lastPoint.y - goalPos.y) : 999;

    if (distToGoal > 35) {
      setStatus('failed');
      return;
    }

    // Run 5-signal classifier
    const evalReport = HumanPulseClassifier.classifyTrajectory(points, duration);
    setReport(evalReport);

    if (evalReport.classification === 'HUMAN') {
      setStatus('verified');
      setTimeout(() => {
        onVerified(evalReport);
      }, 1000);
    } else {
      setStatus('failed');
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setPoints([]);
    setStartTime(null);
    setReport(null);
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #0c101a 0%, #070a10 100%)',
        border: '1px solid #1e293b',
        borderRadius: 16,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        maxWidth: 420,
        color: '#f8fafc',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Title & Badge */}
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={16} color="#38bdf8" />
          <span style={{ fontWeight: 800, fontSize: 14 }}>Human Pulse Kinetic Challenge</span>
        </div>
        <span style={{ fontSize: 11, background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', padding: '2px 8px', borderRadius: 12, fontWeight: 700 }}>
          +{rewardAmountTokens} Tokens
        </span>
      </div>

      <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', lineHeight: 1.4 }}>
        Trace smoothly from the <strong style={{ color: '#0284c7' }}>Blue Orb</strong> to the <strong style={{ color: '#f59e0b' }}>Goal</strong> to verify genuine presence without camera tracking.
      </div>

      {/* Interactive Kinetic Canvas */}
      <div
        style={{
          position: 'relative',
          borderRadius: 12,
          overflow: 'hidden',
          border: status === 'failed' ? '1px solid #ef4444' : status === 'verified' ? '1px solid #10b981' : '1px solid #1e293b',
          background: '#04060a',
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={380}
          height={180}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ cursor: isTracing ? 'grabbing' : 'crosshair', display: 'block' }}
        />

        {status === 'idle' && (
          <div
            style={{
              position: 'absolute',
              top: 14,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(15, 23, 42, 0.85)',
              border: '1px solid #334155',
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 11,
              color: '#94a3b8',
              pointerEvents: 'none',
            }}
          >
            Drag along the guide curve
          </div>
        )}
      </div>

      {/* Feedback banner */}
      {status === 'verified' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#34d399', fontSize: 13, fontWeight: 700 }}>
          <CheckCircle2 size={16} /> Human Attention Verified! Claiming dividend...
        </div>
      )}

      {status === 'failed' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', fontSize: 12, fontWeight: 600 }}>
            <AlertCircle size={15} /> Gesture did not meet human physiological criteria ({report ? `${report.passedCount}/5 signals` : 'Target missed'}).
          </div>
          <button
            type="button"
            onClick={handleReset}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 6,
              padding: '4px 12px',
              color: '#f8fafc',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <RotateCcw size={12} /> Retry Challenge
          </button>
        </div>
      )}

      {/* Zero Surveillance Guarantee */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#64748b' }}>
        <ShieldCheck size={13} color="#0284c7" />
        Zero eye-tracking &bull; Evaluated locally via 5-signal kinetic classification
      </div>
    </div>
  );
};
